/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/prop-types */
import React, {
  useState, useEffect, useRef, useMemo, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactDOM from 'react-dom';
import { Droppable } from 'react-beautiful-dnd';
import {
  List,
  AutoSizer,
  CellMeasurer,
} from 'react-virtualized';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
/** we will need this global cache for setting placeholder logic in week view */
import { useTheme } from 'emotion-theming';
import { getCacheForDroppable } from '../../utils/virtualizationCache';
import useCalendarKeyboardScroll from '../../hooks/useCalendarKeyboardScroll';
import useClearCacheOnLayoutChange from '../../hooks/useClearCacheOnLayoutChange';
import { REVEAL_ADD_EXERCISE_EVENT } from '../../hooks/useTabCyclesWithinItem';
import useIsMobile from '../../../../shared/hooks/useIsMobile';
import {
  setSelectedItems,
  resetExpandedItemsForContextChange,
  setCalendarContainerScrollPosition,
  setIsBeforeRecurringSectionExpanded,
  setIsAfterRecurringSectionExpanded,
  setIsAthleteAddedItemsSectionExpanded,
} from '../../ducks/calendarActions';
import DraggableDivWrapper from './DraggableDivWrapper';
import SelectedItemsTextAndCheckBoxContainerWrapper from './SelectedItemsTextAndCheckBoxContainerWrapper';
import DropHereSection from './DropHereSection';
import ExercisePicker from './ExercisePicker';
import MobileAddExercisePicker from './MobileAddExercisePicker';
import RenderCloneDraggableDivWrapper from './RenderCloneDraggableDivWrapper';
import AfterRecurringVirtualRow from './AfterRecurringVirtualRow';
import AthleteAddedItemsVirtualRow from './AthleteAddedItemsVirtualRow';
import BeforeRecurringVirtualRow from './BeforeRecurringVirtualRow';
import PlaceholderForVirtualizedList from './PlaceholderForVirtualizedList';

const DailyAndRecurring = styled('div')`
  display: flex;
  flex-direction: column;
  height: auto;
`;

const DroppableDiv = styled('div')`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  background: ${(props) => (props.isDragging && props.dragSource !== props.id ? props.theme.colors.surfaceSecondary : props.theme.colors.surface)};
  margin-bottom: ${(props) => (props.isDragging && props.dragSource !== props.id && !props.noMarginBottom && '10px')};
  /* The list must be sized exactly so AutoSizer can hand the virtualized
     List a fixed height: the viewport minus every fixed header, sub header,
     footer and margin above/below it (summed in heightOffset).

     On phones 100vh is the *largest* viewport (browser toolbars collapsed),
     so while the URL bar is showing the bottom of this box sits behind the
     browser chrome and the exercise picker row at the end of the list is
     unreachable. dvh tracks the visible viewport; the vh rule is the
     fallback for browsers without dvh support. */
  height: ${(props) => `calc(100vh - ${props.heightOffset}px)`};
  height: ${(props) => `calc(100dvh - ${props.heightOffset}px)`};
`;

const MemoizedDraggableItems = React.memo(({
  itemForMemo,
  indexForMemo,
  idForMemo,
  measureFunction,
  cacheForMemo,
  isWeekViewForMemo,
  isDayExpandedForMemo,
  effectiveDateForMemo,
  effectiveSelectedProgramDayNumberForMemo,
  onDayExpandForMemo,
}) => (
  <DraggableDivWrapper
    item={itemForMemo}
    index={indexForMemo}
    id={idForMemo}
    measure={measureFunction}
    cache={cacheForMemo}
    isWeekView={isWeekViewForMemo}
    isDayExpanded={isDayExpandedForMemo}
    date={effectiveDateForMemo}
    dayNumber={effectiveSelectedProgramDayNumberForMemo}
    onDayExpand={onDayExpandForMemo}
  />
));

const DraggableContainer = ({
  id,
  date,
  dayKey,
  isWeekView,
  isDayExpanded,
  onDayExpand,
  expandDay,
  columnCount,
}) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const draggedItem = useSelector((state) => state.calendar.data.draggedItem);
  const onBeforeCapture = useSelector((state) => state.calendar.data.onBeforeCapture);
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts,
  );
  const afterRecurringWorkouts = useSelector((state) => state.calendar.data.afterRecurringWorkouts);
  const dragSource = useSelector((state) => state.calendar.data.dragSource);
  const isDragEventHappening = useSelector((state) => state.calendar.data.isDragging);
  const mostRecentlyAddedCalendarItem = useSelector(
    (state) => state.calendar.data.mostRecentlyAddedCalendarItem,
  );
  const isThisDayHorizontallyExpanded = useSelector(
    (state) => String(state.calendar.data.weekViewExpandedDay) === String(dayKey),
  );
  const weekViewExpandedDay = useSelector((state) => state.calendar.data.weekViewExpandedDay);

  const headerHeight = useSelector((state) => state.calendar.data.headerHeight);
  const programType = useSelector((state) => state.calendar.data.programType);
  const isAnalyticsDisabled = useSelector(
    (state) => state.calendar.ui.isAnalyticsDisabled,
  );
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const effectiveDate = date || selectedDate;

  /** date here is actually the dayNumber the prop is just called date */
  const effectiveSelectedProgramDayNumber = date || selectedProgramDayNumber;

  /** pre-compute date key to avoid calling moment() inside useSelector */
  const dateKey = useMemo(
    () => (effectiveDate ? moment(effectiveDate).format('YYYY-MM-DD') : null),
    [effectiveDate],
  );

  /** both selectors run unconditionally to respect rules of hooks.
   * the unused one returns a stable empty array, so no extra re-renders.
   * */
  const formattedDate = useMemo(
    () => moment(effectiveDate).format('YYYY-MM-DD'),
    [effectiveDate],
  );

  const athleteAddedItems = useSelector(
    (state) => state.calendar.data.calendarItems[formattedDate]?.athleteAddedItems,
  );

  /** here we are conditionally setting calendar items to the true calendar items or
   * the non date based items. We are doing it here so we don't have to write conditionals
   * everywhere we use calendarItems
   */
  const EMPTY_EXERCISES = useRef([]).current;
  const nonDateItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems[
      effectiveSelectedProgramDayNumber
    ]?.exercises || EMPTY_EXERCISES,
  );
  const dateItems = useSelector(
    (state) => (dateKey
      ? state.calendar.data.calendarItems[dateKey]?.exercises || EMPTY_EXERCISES
      : EMPTY_EXERCISES),
  );
  const calendarItems = programType === 'free-form-program' ? nonDateItems : dateItems;

  /** Count how many items in THIS day are selected.
   * Returns a primitive (number), so unaffected days skip re-rendering.
   */
  const itemsAreSelected = useSelector((state) => {
    const items = state.calendar.data.selectedItems;
    if (!items || !Object.keys(items).length) return 0;
    let exercises;
    if (programType === 'free-form-program') {
      exercises = state.calendar.data
        .nonDateBasedProgramItems[effectiveSelectedProgramDayNumber]?.exercises;
    } else if (dateKey) {
      exercises = state.calendar.data.calendarItems[dateKey]?.exercises;
    }
    if (!exercises) return 0;
    let count = 0;
    exercises.forEach((calItem) => {
      if (calItem && calItem.assignedId && items[calItem.assignedId]) count += 1;
    });
    return count;
  });

  /** Combine (superset) is allowed when the dragged item is not part of a
   * multi-select group. Returns a primitive boolean so unaffected days
   * skip re-renders. When no drag is active, returns true (stable).
   */
  const isCombineAllowed = useSelector((state) => {
    if (!draggedItem?.assignedId) return true;
    const items = state.calendar.data.selectedItems;
    const isDraggedSelected = !!items[draggedItem.assignedId];
    if (!isDraggedSelected) return true;
    return Object.keys(items || {}).length <= 1;
  });

  /** bind onDayExpand and expandDay to this container's dayKey
   * so downstream components can call them with no arguments */
  const boundOnDayExpand = useCallback(
    () => onDayExpand(dayKey),
    [onDayExpand, dayKey],
  );
  const boundExpandDay = useCallback(
    () => expandDay(dayKey),
    [expandDay, dayKey],
  );

  const listRef = useRef(null);
  const scrollNodeRef = useRef(null);
  const [scrollToIndex, setScrollToIndex] = useState(undefined);

  /** The phone add-exercise picker's open state lives here, not in
   * ExercisePicker: that row is keyed by index, so adding or saving an item
   * remounts it, which would close the picker mid-search. */
  const [isMobilePickerOpen, setIsMobilePickerOpen] = useState(false);
  const openMobilePicker = useCallback(() => setIsMobilePickerOpen(true), []);
  const closeMobilePicker = () => setIsMobilePickerOpen(false);

  useCalendarKeyboardScroll({
    getScrollNode: () => scrollNodeRef.current,
    isActive: viewType === 'dayView'
      || (viewType === 'weekView' && isThisDayHorizontallyExpanded),
  });

  /** navigation header that can have collapsing content so height needs to be dynamic */
  const getHeaderHeight = () => (isTvMode ? 0 : headerHeight);

  const getOtherConditionalHeaderAndSubHeaderHeights = () => {
    const teambuildrLayoutHeaderHeight = isTvMode ? 0 : 49;
    const containerHeaderHeightDayView = programType === 'free-form-program' ? 58 : 80;
    const containerHeaderHeightWeekView = programType === 'free-form-program' ? 58 : 80;
    /** TV-mode subheader bumps to 40px tall to fit calendar name; non-TV stays 32 */
    const nonTvSubHeaderHeight = programType === 'free-form-program' ? 0 : 32;
    const calendarSubHeaderDateAndWeekHeightDayView = isTvMode ? 40 : nonTvSubHeaderHeight;
    const calendarSubHeaderDateAndWeekHeightWeekView = isTvMode ? 40 : nonTvSubHeaderHeight;
    const weekViewToggleFooter = programType === 'free-form-program' ? 0 : (isTvMode ? 66 : 58);
    const wrapperMargin = (() => {
      if (!isTvMode) return 8 + 8;
      if (viewType === 'dayView') return 8 + 8; // margin restored on wrapper in TV day view
      if (viewType === 'weekView') return 8; // WeekContainer padding-top in TV week view
      return 0;
    })();
    const weekViewFooter = programType === 'free-form-program' ? 50 + 16 : 0;
    const dayViewFooter = programType === 'free-form-program' ? 50 : 0;

    return teambuildrLayoutHeaderHeight
    + (viewType === 'weekView' ? containerHeaderHeightWeekView : containerHeaderHeightDayView)
    + (viewType === 'weekView' ? calendarSubHeaderDateAndWeekHeightWeekView : calendarSubHeaderDateAndWeekHeightDayView)
    + (viewType === 'weekView' ? weekViewToggleFooter : 0)
    + wrapperMargin
    + (viewType === 'weekView' ? weekViewFooter : dayViewFooter);
  };

  /** Everything stacked above and below the list, in px, so the DroppableDiv
   * can size itself to the remaining viewport. Any fixed header, the
   * conditional stats bar and the extra padding from the bulk-actions
   * container when items are selected all count. */
  const getListHeightOffset = () => {
    /** the Sets/Reps/Volume stats bar is display: none on phones and not
     * rendered in TV mode, so subtracting it there leaves a dead strip at
     * the bottom of the list */
    const statsBarHeight = isAnalyticsDisabled || isMobile || isTvMode ? 0 : 40;
    const bulkActionsHeight = calendarItems?.length && viewType !== 'weekView' && !isTvMode ? 40 : 0;
    const selectedItemsPadding = (
      (viewType !== 'weekView' && itemsAreSelected)
      || (viewType === 'weekView' && isThisDayHorizontallyExpanded && itemsAreSelected)
    ) ? 20 : 0;
    const expandedWeekDayHeaderHeight = viewType === 'weekView'
      && isThisDayHorizontallyExpanded
      && (programType === 'calendar' || programType === 'free-form-program')
      && calendarItems?.length ? 40 : 0;

    return getHeaderHeight()
      + getOtherConditionalHeaderAndSubHeaderHeights()
      + statsBarHeight
      + bulkActionsHeight
      + selectedItemsPadding
      + expandedWeekDayHeaderHeight;
  };

  useEffect(() => {
    dispatch(setSelectedItems({}));
    dispatch(resetExpandedItemsForContextChange());
    dispatch(setIsBeforeRecurringSectionExpanded(false));
    dispatch(setIsAfterRecurringSectionExpanded(false));
    dispatch(setIsAthleteAddedItemsSectionExpanded(false));
  }, [selectedReduxCalendar,
    selectedDate,
    selectedProgramDayNumber,
    selectedReduxNonDateBasedProgram]);

  useEffect(() => {
    dispatch(setIsBeforeRecurringSectionExpanded(false));
    dispatch(setIsAfterRecurringSectionExpanded(false));
    dispatch(setIsAthleteAddedItemsSectionExpanded(false));
  }, [isThisDayHorizontallyExpanded]);

  const formatDate = (dateToFormat) => `${moment(dateToFormat).format('YYYY-MM-DD')}`;

  /**
   * scroll-start handler (called via react-virtualized's onScroll)
   * Scroll handler that dispatches scroll position ONLY on initial scroll start.
   * This is used to close any open dropdowns (exercise picker, calendar dropdown, etc.)
   * when the user begins scrolling.
   */
  const isScrollingRef = useRef(false);
  const handleScroll = ({ scrollTop }) => {
    if (!isScrollingRef.current) {
      isScrollingRef.current = true;
      dispatch(setCalendarContainerScrollPosition(scrollTop));
    }

    // eslint-disable-next-line no-underscore-dangle
    clearTimeout(window._calendarScrollTimeout);
    // eslint-disable-next-line no-underscore-dangle
    window._calendarScrollTimeout = setTimeout(() => {
      isScrollingRef.current = false;
    }, 150);
  };

  const hasBefore = !!(beforeRecurringWorkouts[formatDate(effectiveDate)]
  && beforeRecurringWorkouts[formatDate(effectiveDate)].length) && programType !== 'free-form-program';

  const hasAfter = !!(afterRecurringWorkouts[formatDate(effectiveDate)]
  && afterRecurringWorkouts[formatDate(effectiveDate)].length) && programType !== 'free-form-program';

  const hasAthleteAddedItems = !!(athleteAddedItems && athleteAddedItems?.length) && programType !== 'free-form-program';

  const lastAutoScrolledIdRef = useRef(null);

  /** this code set the scroll index for the virtual list to scroll to the index
   * of the most recently added item, this is expecially importent
   * when we add an item from the create exercise forms
   */
  useEffect(() => {
    if (!mostRecentlyAddedCalendarItem) return;

    const mostRecentlyAddedId = mostRecentlyAddedCalendarItem.assignedId?.toString();
    if (lastAutoScrolledIdRef.current === mostRecentlyAddedId) return; // already did this one

    const realIndex = calendarItems.findIndex(
      (it) => it?.assignedId === mostRecentlyAddedCalendarItem?.assignedId,
    );
    if (realIndex === -1) return;

    const beforeOffset = hasBefore ? 1 : 0;
    const targetIndex = beforeOffset + realIndex;

    lastAutoScrolledIdRef.current = mostRecentlyAddedId;
    setScrollToIndex(targetIndex);
  }, [mostRecentlyAddedCalendarItem, calendarItems, hasBefore]);

  /** Converge the scroll onto the target row, then clear scrollToIndex so it
   * doesn't stick around and keep bouncing the list back on remounts.
   *
   * The offset for scrollToIndex is computed from CellMeasurer heights, but
   * right after an add the cache was just cleared, so unmeasured rows report
   * the 68px default. The first scroll therefore lands short; as the rendered
   * rows measure, real heights push the target further down. Retry
   * scrollToRow until the scroll position stabilizes (rows between here and
   * the target get measured on each pass), then clear. */
  useEffect(() => {
    if (scrollToIndex === undefined) return undefined;
    let attempts = 0;
    let lastScrollTop = null;
    let timer;
    const tryScroll = () => {
      const list = listRef.current;
      const node = scrollNodeRef.current;
      const currentTop = node ? node.scrollTop : null;
      if (attempts > 0 && currentTop !== null && currentTop === lastScrollTop) {
        /** Position stable across a full retry interval — converged. */
        setScrollToIndex(undefined);
        return;
      }
      lastScrollTop = currentTop;
      if (list) list.scrollToRow(scrollToIndex);
      attempts += 1;
      if (attempts < 8) {
        timer = setTimeout(tryScroll, 100);
      } else {
        setScrollToIndex(undefined);
      }
    };
    timer = setTimeout(tryScroll, 0);
    return () => clearTimeout(timer);
  }, [scrollToIndex]);

  const totalRowCount = (hasBefore ? 1 : 0)
    + calendarItems.length
    + 1 /** exercise picker */
    + (hasAfter ? 1 : 0)
    + (hasAthleteAddedItems ? 1 : 0)
    + (onBeforeCapture ? 1 : 0);

  /** The cache is global per droppable so the drag manager can read the
   * dragged row's height for the cross-container placeholder. */
  const cache = getCacheForDroppable(id);
  /** Rows re-measure only when the cache is cleared. Clear it for anything
   * that changes row heights or shifts row indexes: this day's items, the
   * fake rows (recurring, athlete-added, drop-here), and the column width,
   * which flex redistributes whenever any day expands or the column count
   * changes. Rows that change their own height (expand/collapse, table rows,
   * placeholder) call measure() themselves. Drag flags are deliberately not
   * in this list. */
  useClearCacheOnLayoutChange(cache, [
    calendarItems,
    beforeRecurringWorkouts[formattedDate],
    afterRecurringWorkouts[formattedDate],
    athleteAddedItems,
    hasBefore,
    hasAfter,
    hasAthleteAddedItems,
    !!onBeforeCapture,
    isDayExpanded,
    weekViewExpandedDay,
    columnCount,
    isWeekView,
    isTvMode,
    isMobile,
    viewType,
    programType,
  ]);
  /** AutoSizer reports size changes the inputs above can't see: sidebar
   * open/close, window resize, zoom, and the list height the drop-here row
   * sizes itself from. */
  const lastListSizeRef = useRef({ width: null, height: null });

  /** After the sidebar open/close CSS transition (250ms) finishes, the
   *  AutoSizer's scroll-trigger resize detection can miss the final width.
   *  Call AutoSizer._onResize() directly via ref so it re-reads
   *  parentNode.offsetWidth and updates the List width. */
  const autoSizerRef = useRef(null);
  const sidebarMountedRef = useRef(true);
  const { colors } = useTheme();
  useEffect(() => {
    if (sidebarMountedRef.current) {
      sidebarMountedRef.current = false;
      return () => {};
    }
    const timer = setTimeout(() => {
      // eslint-disable-next-line no-underscore-dangle
      if (autoSizerRef.current && autoSizerRef.current._onResize) {
        // eslint-disable-next-line no-underscore-dangle
        autoSizerRef.current._onResize();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isPrimaryAnimatedSideBarOpen]);

  /** compute offsets */
  const beforeBlockOffset = hasBefore ? 1 : 0;
  const onBeforeCaptureOffset = onBeforeCapture ? 1 : 0;
  const onBeforeCaptureIndex = beforeBlockOffset
  + calendarItems.length;
  const placeholderIndex = onBeforeCaptureIndex + onBeforeCaptureOffset;

  /** Tab from the day's last item targets Add Exercise, but the virtualized
   * list unmounts the picker row once it scrolls out of view. Scroll it in,
   * then focus the button as soon as the row renders. */
  const exerciseListRef = useRef(null);
  const pickerRowIndex = placeholderIndex + (onBeforeCapture ? 0 : 1);
  useEffect(() => {
    const node = exerciseListRef.current;
    if (!node) return undefined;
    let frame;
    const onReveal = () => {
      cancelAnimationFrame(frame);
      listRef.current?.scrollToRow(pickerRowIndex);
      let attempts = 0;
      const focusWhenMounted = () => {
        const button = node.querySelector('[data-add-exercise-button]');
        if (button) {
          button.focus();
          return;
        }
        attempts += 1;
        if (attempts < 10) frame = requestAnimationFrame(focusWhenMounted);
      };
      frame = requestAnimationFrame(focusWhenMounted);
    };
    node.addEventListener(REVEAL_ADD_EXERCISE_EVENT, onReveal);
    return () => {
      node.removeEventListener(REVEAL_ADD_EXERCISE_EVENT, onReveal);
      cancelAnimationFrame(frame);
    };
  }, [pickerRowIndex]);

  const rowRenderer = ({
    index,
    key,
    style,
    parent,
    isUsingPlaceholder,
    /** hideExercisePicker, */
    showPlaceholder,
    isDraggingOver,
  }) => (
    <CellMeasurer
      cache={cache}
      columnIndex={0}
      key={key}
      parent={parent}
      rowIndex={index}
    >
      {({ measure, registerChild }) => {
        let content = null;

        /** compute offsets */
        const placeholderOffset = isUsingPlaceholder && !onBeforeCapture ? 1 : 0;
        const exercisePickerIndex = placeholderIndex + placeholderOffset;
        const afterBlockIndex = exercisePickerIndex + 1;
        const athleteAddedItemsIndex = hasAfter ? exercisePickerIndex + 2 : exercisePickerIndex + 1;

        // const placeholderOffset = isUsingPlaceholder && !onBeforeCapture ? 1 : 0;
        // const exercisePickerIndex = placeholderIndex + placeholderOffset;
        // const afterBlockOffset = hasAfter ? 1 : 0;
        // const afterBlockIndex = hasAfter
        //   ? exercisePickerIndex + 1
        //   : 0;
        // const athleteAddedItemsIndex = exercisePickerIndex + afterBlockOffset;

        /** Before recurring section */
        if (hasBefore && index === 0) {
          content = (
            <BeforeRecurringVirtualRow
              measure={measure}
              cache={cache}
              date={effectiveDate}
              dayKey={dayKey}
              onDayExpand={boundOnDayExpand}
              isDayExpanded={isDayExpanded}
            />
          );
        }

        /** Real calendar items */
        if (index >= (hasBefore ? 1 : 0) && index < (hasBefore ? 1 : 0)
          + calendarItems.length) {
          const realIndex = index - (hasBefore ? 1 : 0);
          const item = calendarItems[realIndex];

          content = (
            <>
              {item ? (
                <MemoizedDraggableItems
                  key={`${item.assignedId}-${item.preSave ? 'pre' : 'saved'}`}
                  itemForMemo={item}
                  indexForMemo={realIndex}
                  idForMemo={id}
                  measureFunction={measure}
                  cacheForMemo={cache}
                  isWeekViewForMemo={isWeekView}
                  isDayExpandedForMemo={isDayExpanded}
                  effectiveDateForMemo={effectiveDate}
                  effectiveSelectedProgramDayNumberForMemo={effectiveSelectedProgramDayNumber}
                  onDayExpandForMemo={boundOnDayExpand}
                />
              ) : null}
            </>
          );
        }

        /** Drop Here prompt if less than 2 items */
        if (onBeforeCapture && index === onBeforeCaptureIndex) {
          /** figuring out the height that the drop here seciton needs to be to take up
           * the remaining height in the doppable area to let the user see the dropzone
           */
          const totalHeight = parent.props.height; /** AutoSizer List height */
          const usedHeightAbove = hasBefore ? 68 : 0;
          const exercisePickerHeight = hasAfter || hasAthleteAddedItems ? 45 : 50;
          const afterHeight = hasAfter ? 68 : 0;
          const athleteAddedItemsHeight = hasAthleteAddedItems ? 68 : 0;

          /** Compute remaining height */
          const remainingHeight = Math.max(
            68, /** min height */
            totalHeight
            - usedHeightAbove
            - exercisePickerHeight
            - afterHeight
            - athleteAddedItemsHeight,
          );
          content = (
            <div style={{
              height: calendarItems.length ? 100 : remainingHeight,
              display: 'flex',
              border: !calendarItems.length && `1px dashed ${colors.hover}`,
              borderRadius: 4,
            }}
            >
              <DropHereSection id={id} />
            </div>
          );
        }

        /** Placeholder row */
        if (isUsingPlaceholder && !onBeforeCapture && index === placeholderIndex) {
          content = (
            <PlaceholderForVirtualizedList
              measure={measure}
              showPlaceholder={showPlaceholder}
              isDraggingOver={isDraggingOver}
              droppableId={id}
            />
          );
        }

        if (index === exercisePickerIndex) {
          content = (
            <ExercisePicker
              isWeekView={isWeekView}
              calendarItemsHaveLength={calendarItems?.length > 0}
              hasBefore={hasBefore}
              hasAfter={hasAfter || hasAthleteAddedItems}
              date={effectiveDate}
              dayNumber={effectiveSelectedProgramDayNumber}
              expandDay={boundExpandDay}
              onOpenMobilePicker={openMobilePicker}
            />
          );
        }

        /* After recurring section */
        if (hasAfter && index === afterBlockIndex) {
          content = (
            <AfterRecurringVirtualRow
              measure={measure}
              cache={cache}
              date={effectiveDate}
              dayKey={dayKey}
              onDayExpand={boundOnDayExpand}
              isDayExpanded={isDayExpanded}
            />
          );
        }

        /* Athlete Added Items Section */
        if (hasAthleteAddedItems && index === athleteAddedItemsIndex) {
          content = (
            <AthleteAddedItemsVirtualRow
              measure={measure}
              cache={cache}
              date={effectiveDate}
              dayKey={dayKey}
              onDayExpand={onDayExpand}
              isDayExpanded={isDayExpanded}
            />
          );
        }

        return (
          <div ref={registerChild} style={{ ...style }}>
            {content}
          </div>
        );
      }}
    </CellMeasurer>
  );

  return (
    <>
      {!!calendarItems.length && (!isWeekView
      || (isWeekView && isThisDayHorizontallyExpanded)
      ) && (
      <SelectedItemsTextAndCheckBoxContainerWrapper dayKey={dayKey} />
      )}
      <DailyAndRecurring
        onBeforeCapture={onBeforeCapture}
        calendarItemLength={calendarItems?.length}
      >
        {/** Note: beforeRecurring and afterRecurring are now
         * rendered inside the list as fake rows */}
        <Droppable
          isCombineEnabled={
            draggedItem?.type !== 'session_break'
            && isCombineAllowed
          }
          droppableId={id}
          mode='virtual'
          renderClone={(provided, snapshot, rubric) => {
            const item = calendarItems[rubric.source.index];
            return (
              <RenderCloneDraggableDivWrapper
                item={item}
                index={rubric.source.index}
                id={id}
                provided={provided}
                snapshot={snapshot}
                date={effectiveDate}
                dayNumber={effectiveSelectedProgramDayNumber}
                dayKey={dayKey}
              />
            );
          }}
        >
          {(droppableProvided, snapshot) => {
            const showPlaceholder = snapshot?.isUsingPlaceholder && snapshot?.draggingOverWith
             && !snapshot?.draggingOverWith?.includes(id) && !snapshot?.draggingFromThisWith;
            const totalItems = totalRowCount + 1; /** permanent placeholder is the +1 */
            return (
              <DroppableDiv
                noMarginBottom={(afterRecurringWorkouts[formatDate(effectiveDate)]?.length
                  && afterRecurringWorkouts[formatDate(effectiveDate)]?.length) || (athleteAddedItems?.length) || viewType === 'weekView'}
                isDragging={isDragEventHappening}
                dragSource={dragSource}
                calendarItemLength={calendarItems?.length}
                id={id}
                onBeforeCapture={onBeforeCapture}
                heightOffset={getListHeightOffset()}
                ref={exerciseListRef}
                data-exercise-list
              >
                <AutoSizer ref={autoSizerRef}>
                  {({ width, height }) => {
                    if (lastListSizeRef.current.width !== width
                      || lastListSizeRef.current.height !== height) {
                      lastListSizeRef.current = { width, height };
                      cache.clearAll();
                    }
                    return (
                      <List
                        {...droppableProvided.droppableProps}
                        style={{
                          backgroundColor: colors.surface,
                          scrollbarWidth: (isWeekView || isTvMode) ? 'none' : 'thin',
                          overflowY: 'scroll',
                          /** phones: keep edge scrolls from chaining into body
                           * rubber-banding / pull-to-refresh next to the touch
                           * drag surface */
                          ...(isMobile && { overscrollBehavior: 'contain' }),
                        }}
                        height={height}
                        width={width}
                        onScroll={handleScroll}
                        /** scroll to index allows us to scroll to
                         * an index of a recently added item
                         * but we must clear the item right after we scroll to
                         * index so it doesn't stay in redux and continue to
                         * bounce to that index on remounts */
                        scrollToIndex={scrollToIndex}
                        rowCount={totalItems}
                        rowRenderer={(rowRenderArgs) => rowRenderer({
                          ...rowRenderArgs, isUsingPlaceholder: true, draggingFromThisWith: snapshot?.draggingFromThisWith, hideExercisePicker: snapshot?.isUsingPlaceholder && snapshot?.draggingOverWith?.includes('sideBar'), showPlaceholder, isDraggingOver: snapshot?.isDraggingOver,
                        })}
                        rowHeight={cache.rowHeight}
                        deferredMeasurementCache={cache}
                        overscanRowCount={1}
                        /** this estimated size helps to make sure we don't get
                         * stuck only being able to scroll half way
                         * we will over estimate the scrollable area by a lot to make sure
                        */
                        estimatedRowSize={500}
                        ref={(ref) => {
                          listRef.current = ref;
                          if (ref) {
                            /** bridge inner DOM node to react-beautiful-dnd */
                            // eslint-disable-next-line react/no-find-dom-node
                            const whatHasMyLifeComeTo = ReactDOM.findDOMNode(ref);
                            if (whatHasMyLifeComeTo instanceof HTMLElement) {
                              droppableProvided.innerRef(whatHasMyLifeComeTo);
                              scrollNodeRef.current = whatHasMyLifeComeTo;
                            }
                          } else {
                            scrollNodeRef.current = null;
                          }
                        }}
                      />
                    );
                  }}
                </AutoSizer>
              </DroppableDiv>
            );
          }}
        </Droppable>
      </DailyAndRecurring>
      {isMobilePickerOpen && (
        <MobileAddExercisePicker
          isOpen
          onClose={closeMobilePicker}
          date={effectiveDate}
          dayNumber={effectiveSelectedProgramDayNumber}
        />
      )}
    </>
  );
};

DraggableContainer.propTypes = {
  id: PropTypes.string.isRequired,
  date: PropTypes.string,
  dayKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isWeekView: PropTypes.bool,
  isDayExpanded: PropTypes.bool,
  onDayExpand: PropTypes.func,
  expandDay: PropTypes.func,
  /** number of day columns on screen; their widths change together */
  columnCount: PropTypes.number,
};

DraggableContainer.defaultProps = {
  date: null,
  dayKey: null,
  isWeekView: false,
  isDayExpanded: false,
  onDayExpand: () => {},
  expandDay: () => {},
  columnCount: 1,
};

export default React.memo(DraggableContainer);
